/** The longest access-token lifetime Cognito can be configured to grant. */
const LONGEST_POSSIBLE_TTL_MS = 24 * 60 * 60 * 1000;

/** Refresh inside this much of expiry, and no more often than this. */
const REFRESH_MARGIN_MS = 10 * 60 * 1000;

/**
 * How often to ask whether the token is due.
 *
 * A heartbeat rather than a timer at the token's halfway point, because a timer deadline
 * does not accrue while the machine is suspended: a lid closed for longer than the token's
 * life would wake to a dead token and a loop still capturing.
 */
export const TOKEN_HEARTBEAT_MS = 60 * 1000;

/**
 * How long to wait for a refresh before carrying on without it. Nothing else bounds the
 * call, and an unbounded wait costs either the start or all later renewal.
 */
export const REFRESH_DEADLINE_MS = 10 * 1000;

/**
 * Whether the bearer token should be refreshed now.
 *
 * @param expiresAtSeconds Access-token expiry in seconds since the epoch.
 * @param lastRefreshedAtMs When a refresh last succeeded, or 0 if none has.
 */
export const isBearerTokenRefreshDue = (
  expiresAtSeconds: number | undefined,
  lastRefreshedAtMs = 0
): boolean => {
  // Outranks the expiry: a token can read as due the instant after it was refreshed, which
  // without a floor is one Cognito call per beat. Clamped because a negative gap means the
  // clock moved backwards, not that a refresh just happened.
  const sinceLastRefreshMs = Date.now() - lastRefreshedAtMs;
  if (sinceLastRefreshMs >= 0 && sinceLastRefreshMs < REFRESH_MARGIN_MS) {
    return false;
  }
  if (expiresAtSeconds === undefined || !Number.isFinite(expiresAtSeconds)) {
    return true;
  }
  const remainingMs = expiresAtSeconds * 1000 - Date.now();
  // Longer than Cognito can grant means the local clock is wrong, so the expiry cannot be
  // compared against it.
  if (remainingMs > LONGEST_POSSIBLE_TTL_MS) {
    return true;
  }
  return remainingMs <= REFRESH_MARGIN_MS;
};
